import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import {
    checkIfDateIsValid,
    getTodayDateString,
} from 'components/inputs/dateHelpers';

type Props = {
    value: string | null | undefined;
    label?: string;
    disabled?: boolean;
    onChange: (arg1: string) => void;
    fullWidth?: boolean;
    shouldDebounce?: boolean;
    InputProps?: TextFieldProps['InputProps'];
    error?: boolean;
    helperText?: string;
    applyDefaultValueIfNull?: boolean;
    min?: string;
    isValidDateProp?: (date: string) => boolean;
};

const DEBOUNCE_INTERVAL = 1000;

const defaultValue = getTodayDateString();

const triggerDateChange = (
    date: string,
    callback: (arg: string) => void,
    setIsValidDate: React.Dispatch<React.SetStateAction<boolean>>,
) => {
    const [year, month, day] = date.split('-').map((n) => parseInt(n, 10));

    if (checkIfDateIsValid(year, month, day)) {
        setIsValidDate(true);
        callback(date);
    } else {
        setIsValidDate(false);
    }
};

const debouncedTrigger = _.debounce(triggerDateChange, DEBOUNCE_INTERVAL);

export default function DatePicker({
    fullWidth = false,
    shouldDebounce = false,
    disabled = false,
    applyDefaultValueIfNull = true,
    label = undefined,
    value,
    error = undefined,
    helperText = undefined,
    InputProps = undefined,
    onChange: propsOnChange,
    min = undefined,
    isValidDateProp = undefined,
}: Props): React.ReactElement {
    const propsToInput =
        value != null || !applyDefaultValueIfNull ? value : defaultValue;
    const [inputDate, setInputDate] = useState<string | null | undefined>(
        propsToInput,
    );
    const [isValidDate, setIsValidDate] = useState<boolean>(true);

    useEffect(() => setInputDate(propsToInput), [value]);

    const onChange: TextFieldProps['onChange'] = useCallback(
        (
            e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
        ): void => {
            const date = e.target.value;
            const year = date.split('-')[0];

            // Restrict year to 4 digits
            if (year.length > 4) {
                return;
            }

            setInputDate(date);

            if (shouldDebounce)
                debouncedTrigger(date, propsOnChange, setIsValidDate);
            else {
                propsOnChange(date);
                if (date === '') setIsValidDate(false);
                else if (isValidDateProp !== undefined) {
                    setIsValidDate(isValidDateProp(date));
                } else setIsValidDate(true);
            }
        },
        [isValidDateProp, setIsValidDate, propsOnChange],
    );

    return (
        <TextField
            variant="standard"
            type="date"
            label={label}
            disabled={disabled}
            value={inputDate}
            onChange={onChange}
            error={error}
            helperText={error && helperText}
            // @ts-expect-error [TS migration] Was not detected by flow
            locale="fr"
            fullWidth={fullWidth}
            InputProps={InputProps}
            inputProps={{
                maxLength: 10,
                ...(min === undefined ? {} : { min: min }),
            }}
            sx={{
                '& .MuiInputBase-input': {
                    color: isValidDate ? '' : 'orange',
                },
            }}
        />
    );
}
